import $ from 'jquery'
import { SfLog } from '@app/adapters/logger'
import {
  PADDING,
  ROOT_ID,
  X_STEP,
  Y_STEP,
  calculateGraphView,
  toDependencyGraph,
} from '@app/application/reconciliation/dependency-tree'
import { EchartsHost } from '@app/shared/echarts/echarts-host'

const MAX_VIEWPORT_SHARE = 0.7

onDomLoad(function ($dom) {
  $dom.on('click', '.dependents-tree-wrapper', function () {
    const container = this.querySelector("[data-behaviour='dependencies-tree']")
    if (!container || container.dataset.rendered) return

    // Mark as rendered up front so the in-flight request can't be fired twice
    // by repeated toggles of the <details> wrapper.
    container.dataset.rendered = 'true'

    Silverfin.Spinner.start()
    $.ajax({
      url: container.dataset.url,
      dataType: 'json',
      success: (treeData) => {
        renderDependenciesGraph(container, treeData)
          .catch((error) => {
            container.dataset.rendered = ''
            SfLog.error('Failed to render dependencies graph:', { error })
          })
          .finally(() => Silverfin.Spinner.success())
      },
      error: (_jqXHR, _status, error) => {
        container.dataset.rendered = ''
        SfLog.error('Failed to load dependencies graph:', { error })
        Silverfin.Spinner.failed()
      },
    })
  })
})

async function renderDependenciesGraph(container, treeData) {
  if (!Array.isArray(treeData) || !treeData[0]) return

  const { nodes, links } = toDependencyGraph(treeData[0])

  // Scale the abstract coordinates to pixels, normalised so the top-left of the
  // graph sits at PADDING.
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const node of nodes) {
    if (node.x < minX) minX = node.x
    if (node.x > maxX) maxX = node.x
    if (node.y < minY) minY = node.y
    if (node.y > maxY) maxY = node.y
  }

  const data = nodes.map((node) => ({
    id: node.id,
    name: node.name,
    tooltip: node.tooltip,
    x: (node.x - minX) * X_STEP + PADDING,
    y: (node.y - minY) * Y_STEP + PADDING,
    label: { position: node.labelPosition },
  }))

  const root = nodes.find((node) => node.id === ROOT_ID)
  const view = calculateGraphView({
    width: (maxX - minX) * X_STEP,
    height: (maxY - minY) * Y_STEP,
    rootY: (root.y - minY) * Y_STEP,
    maxHeight: window.innerHeight * MAX_VIEWPORT_SHARE,
  })
  container.style.height = `${view.height}px`

  const hint = container.parentElement.querySelector(
    "[data-role='dependencies-tree-hint']"
  )

  if (hint) {
    hint.hidden = !view.layout
  }

  // Nothing disposes this host; the guard only satisfies mount()'s nullable
  // return.
  const chart = await new EchartsHost().mount(container)
  if (!chart) return

  chart.setOption({
    tooltip: {
      trigger: 'item',
      formatter: (params) =>
        params.dataType === 'edge' ? '' : params.data.tooltip ?? params.name,
    },
    series: [
      {
        type: 'graph',
        layout: 'none',
        // Trees can be large; allow pan/zoom since there's no collapse/expand.
        roam: true,
        ...view.layout,
        symbolSize: 12,
        edgeSymbol: ['none', 'arrow'],
        edgeSymbolSize: 9,
        itemStyle: { color: '#fff', borderColor: 'steelblue', borderWidth: 2 },
        lineStyle: { color: '#ccc', width: 1.5 },
        label: { show: true, fontSize: 12, color: '#333' },
        emphasis: { focus: 'adjacency' },
        data,
        links,
      },
    ],
  })
}
